import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import {
  getBazaarsQuery,
  getSellerAddressesQuery,
} from "../../queries";
import TableContainer from "../Common/TableContainer";
import {
  bindAddressToSeller,
  bindTerminalToSeller,
  removeAddressFromSeller,
} from "../../api/seller";
import DeleteModal from "../Common/DeleteModal";
import {
  Row,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

export function SellerAddress() {
  const [modal, setModal] = useState(false);
  const { sellerId } = useParams();
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === "rs" ? "ru" : i18n.language;

  const { data: addresses, isError, error } = useQuery({
    ...getSellerAddressesQuery(sellerId),
  });

  useEffect(() => {
    if (isError) {
      alert(error?.response?.data?.error?.message || error?.message);
    }
  }, [isError, error]);

  const { data: bazaars } = useQuery({
    ...getBazaarsQuery({
      size: "1000",
      page: 1,
    }),
  });

  const toggle = () => {
    setModal(!modal);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      bazaarId: "",
      name: "",
      address1: "",
      description: "",
    },
    validationSchema: Yup.object({
      bazaarId: Yup.string().required("Please select bazaar"),
      name: Yup.string().required("Please enter name"),
      address1: Yup.string().required("Please enter address"),
      description: Yup.string().required("Please enter description"),
    }),
    onSubmit: (values, { resetForm }) => {
      bindAddressToSeller(sellerId, values)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["seller-addresses", sellerId],
          });
          resetForm();
          toggle();
        })
        .catch((err) => {
          alert(err?.response?.data?.error?.message || err?.message);
        });
    },
  });

  const columns = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: t("Address"),
        accessorKey: "address1",
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            <button
              className="btn btn-sm btn-danger"
              style={{ marginLeft: "10px" }}
              onClick={() => setDeleteModal(row.original.id)}
            >
              <i className="ri-delete-bin-6-fill"></i>
            </button>
          </>
        ),
      },
    ],
    []
  );

  const onDelete = () => {
    removeAddressFromSeller(sellerId, deleteModal)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["seller-addresses", sellerId],
        });
      })
      .catch((err) => {
        alert(err?.response?.data?.error?.message || err?.message);
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  return (
    <>
      <div className="card-body pt-0" style={{ padding: 0 }}>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={onDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <div>
          <Row className="g-4 mb-3 align-items-center">
            <div className="col-sm">
              <div>
                <h5 className="card-title mb-0">{t("Addresses")}</h5>
              </div>
            </div>
            <div className="col-sm-auto">
              <div>
                <button
                  type="button"
                  className="btn btn-success add-btn"
                  id="create-btn"
                  onClick={() => setModal(true)}
                >
                  <i className="ri-add-line align-bottom me-1"></i>{" "}
                  {t("create")}
                </button>
              </div>
            </div>
          </Row>
          {addresses?.data?.data?.length > 0 ? (
            <TableContainer
              columns={columns}
              data={addresses?.data?.data || []}
              isAddUserList={false}
              customPageSize={10}
              className="custom-header-css"
              SearchPlaceholder="Search for user, email, phone, status or something..."
            />
          ) : (
            <div className="text-center p-3 text-muted">
              <h5>{t("No Data")}</h5>
            </div>
          )}
        </div>
        <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
          <ModalHeader className="bg-light p-3" toggle={toggle}>
            {t("create")}
          </ModalHeader>
          <Form
            className="tablelist-form"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <div className="mb-3">
                <Label htmlFor="choices-text-input" className="form-label">
                  {t("Bazaar")}
                </Label>

                <Select
                  placeholder={t("Bazaar")}
                  value={
                    bazaars?.data?.data
                      ?.map((bazaar) => {
                        const title =
                          bazaar?.locales?.find((l) => l.localeKey === currentLang)
                            ?.title || bazaar?.locales?.[0]?.title;
                        return {
                          value: bazaar.id,
                          label: title,
                        };
                      })
                      .find(
                        (option) => option.value === validation.values.bazaarId
                      ) || ""
                  }
                  options={bazaars?.data?.data?.map((bazaar) => {
                    const title =
                      bazaar?.locales?.find((l) => l.localeKey === currentLang)
                        ?.title || bazaar?.locales?.[0]?.title;
                    return {
                      value: bazaar.id,
                      label: title,
                    };
                  })}
                  onChange={(selectedOption) =>
                    validation.setFieldValue("bazaarId", selectedOption.value)
                  }
                />
                {validation.errors.bazaarId ? (
                  <FormFeedback type="invalid">
                    {validation.errors.bazaarId}
                  </FormFeedback>
                ) : null}
              </div>
              <div className="mb-3">
                <Label htmlFor="name-field" className="form-label">
                  {t("Seller Name")}
                </Label>
                <Input
                  name="name"
                  id="name-field"
                  className="form-control"
                  placeholder={t("Seller Name")}
                  type="text"
                  validate={{
                    required: { value: true },
                  }}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.name || ""}
                  invalid={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                />
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </div>
              <div className="mb-3">
                <Label htmlFor="description-field" className="form-label">
                  {t("Spot Number")}
                </Label>
                <Input
                  name="description"
                  id="description-field"
                  className="form-control"
                  placeholder={t("Spot Number")}
                  type="text"
                  validate={{
                    required: { value: true },
                  }}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.description || ""}
                  invalid={
                    validation.touched.description &&
                      validation.errors.description
                      ? true
                      : false
                  }
                />
                {validation.touched.description &&
                  validation.errors.description ? (
                  <FormFeedback type="invalid">
                    {validation.errors.description}
                  </FormFeedback>
                ) : null}
              </div>
              <div className="mb-3">
                <Label htmlFor="address1-field" className="form-label">
                  {t("Address")}
                </Label>
                <Input
                  name="address1"
                  id="address1-field"
                  className="form-control"
                  placeholder={t("Address")}
                  type="text"
                  validate={{
                    required: { value: true },
                  }}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.address1 || ""}
                  invalid={
                    validation.touched.address1 && validation.errors.address1
                      ? true
                      : false
                  }
                />
                {validation.touched.address1 && validation.errors.address1 ? (
                  <FormFeedback type="invalid">
                    {validation.errors.address1}
                  </FormFeedback>
                ) : null}
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="hstack gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setModal(false);
                  }}
                >
                  {t("Close")}
                </button>

                <button type="submit" className="btn btn-success">
                  {t("create")}
                </button>
              </div>
            </ModalFooter>
          </Form>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </>
  );
}
